import type { NoMdast } from './remarkMencoes.ts';

/**
 * UC citada no texto da solicitação (#6587): acha os números com cara de UC e,
 * dos que existem no cadastro, faz trechos clicáveis.
 *
 * Só vira link o número confirmado no cadastro (`conhecidos`, normalizado como o
 * Faturamento compara: sem ponto, traço e zeros na frente). É o que separa UC de
 * CPF, valor e número de fatura, já que a máscara não distingue: há UC gravada
 * como "000.000.000-00".
 *
 * Função pura em .ts, sem imports de runtime, para rodar no `node --test`.
 */

// 8 a 14 dígitos, com ponto ou traço entre eles. 99% das UCs cadastradas têm de 8
// a 12; abaixo disso só aparecem anos, faturas e valores. As bordas não deixam
// pegar o pedaço de um número maior nem a parte inteira de "12.345.678,90".
// Também ficam de fora: valor logo depois de "R$" (sem centavos, "R$ 12.345.678" não
// tem vírgula para a borda barrar) e a raiz de um CNPJ com máscara ("12.345.678/0001-90"
// deixaria "12345678" como candidato, e "00.123.456/..." viraria a UC "123456").
const CANDIDATO = /(?<![\d.,\-/])(?<![Rr]\$\s*)(?!\d{2}\.?\d{3}\.?\d{3}\/\d{4}-?\d{2})\d(?:[.\-]?\d){7,13}(?![\d]|[.,\-]\d)/g;

export const normalizarNumeroUc = (v: string | null | undefined): string =>
    String(v ?? '').replace(/[.\-\s]/g, '').replace(/^0+/, '');

/** Números candidatos a UC no texto, já normalizados e sem repetição. */
export function candidatosUc(texto: string | null | undefined): string[] {
    const achados = String(texto ?? '').match(CANDIDATO) ?? [];
    return [...new Set(achados.map(normalizarNumeroUc).filter(Boolean))];
}

export interface ParteUc {
    texto: string;
    /** Número normalizado, quando o trecho é uma UC do cadastro. */
    uc?: string;
}

export function partesComUc(texto: string, conhecidos: ReadonlySet<string>): ParteUc[] {
    if (!texto || conhecidos.size === 0) return [{ texto }];
    const partes: ParteUc[] = [];
    let fim = 0;
    for (const m of texto.matchAll(CANDIDATO)) {
        const numero = normalizarNumeroUc(m[0]);
        if (!conhecidos.has(numero)) continue;
        const inicio = m.index ?? 0;
        if (inicio > fim) partes.push({ texto: texto.slice(fim, inicio) });
        partes.push({ texto: m[0], uc: numero });
        fim = inicio + m[0].length;
    }
    if (fim < texto.length) partes.push({ texto: texto.slice(fim) });
    return partes.length ? partes : [{ texto }];
}

/** Atributo que o nó leva até o React; lido no `components.span`. */
export const ATRIBUTO_UC = 'data-uc';

/**
 * Troca as UCs em nós `text` por nós próprios. Não entra em link: um número que já
 * é parte de um link continua sendo o link.
 */
export const aplicarUcs = (no: NoMdast, conhecidos: ReadonlySet<string>): NoMdast => {
    if (!no.children || no.children.length === 0 || no.type === 'link' || no.type === 'linkReference') return no;
    const novos: NoMdast[] = [];
    for (const filho of no.children) {
        if (filho.type !== 'text' || typeof filho.value !== 'string') {
            novos.push(aplicarUcs(filho, conhecidos));
            continue;
        }
        const partes = partesComUc(filho.value, conhecidos);
        if (partes.every(p => !p.uc)) { novos.push(filho); continue; }
        for (const p of partes) {
            if (p.uc) novos.push({
                type: 'ucCitada',
                data: { hName: 'span', hProperties: { [ATRIBUTO_UC]: p.uc }, hChildren: [{ type: 'text', value: p.texto }] },
            });
            else if (p.texto) novos.push({ type: 'text', value: p.texto });
        }
    }
    no.children = novos;
    return no;
};

/** Plugin remark. Vai depois do remark-breaks, como o remarkMencoes. */
export const remarkUcs = (conhecidos: ReadonlySet<string>) => () => (arvore: NoMdast) => {
    aplicarUcs(arvore, conhecidos);
};
